import React, { useState, useLayoutEffect, useRef } from 'react';
import Container from './Container';
import { gsap } from 'gsap';

const ServicesHowWeWork = () => {
    const [openIndex, setOpenIndex] = useState(1);
    const panelRefs = useRef({});
    const prevOpenRef = useRef(1);

    const features = [
        {
            title: "Fast & Reliable Deliveries",
            description: "Our optimized logistics network ensures your cargo reaches its destination on time, every time, with real-time tracking and monitoring."
        },
        {
            title: "Global Network & Connectivity",
            description: "With an extensive international network of carriers and partners, we provide seamless air freight solutions across multiple destinations worldwide."
        },
        {
            title: "Secure & Safe Cargo Handling",
            description: "Your shipments are protected with comprehensive insurance coverage and secure handling protocols throughout the entire journey."
        },
        {
            title: "Flexible & Cost-Effective Solutions",
            description: "We offer tailored logistics solutions that fit your budget and requirements, from express delivery to cost-effective bulk shipping."
        },
        {
            title: "Specialized Freight Handling",
            description: "Our team handles specialized cargo including hazardous materials, temperature-controlled goods, and oversized shipments with expertise."
        },
        {
            title: "Documentation Support",
            description: "Complete documentation assistance including customs clearance, shipping documents, and compliance support for hassle-free logistics."
        }
    ];

    useLayoutEffect(() => {
        // Initial state: only openIndex panel is expanded
        features.forEach((_, idx) => {
            const el = panelRefs.current[idx];
            if (!el) return;
            gsap.set(el, { overflow: 'hidden' });
            if (idx === openIndex) {
                gsap.set(el, { height: 'auto', opacity: 1, pointerEvents: 'auto' });
            } else {
                gsap.set(el, { height: 0, opacity: 0, pointerEvents: 'none' });
            }
        });
        prevOpenRef.current = openIndex;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useLayoutEffect(() => {
        const prev = prevOpenRef.current;
        if (prev === openIndex) return;

        // Close previous panel with animation
        if (prev !== -1 && panelRefs.current[prev]) {
            const prevEl = panelRefs.current[prev];
            gsap.killTweensOf(prevEl);
            const currentHeight = prevEl.scrollHeight;
            gsap.set(prevEl, { height: currentHeight, overflow: 'hidden' });
            gsap.to(prevEl, {
                height: 0,
                opacity: 0,
                duration: 0.35,
                ease: 'power2.inOut',
                onComplete: () => gsap.set(prevEl, { pointerEvents: 'none' })
            });
        }

        // Open current panel with animation
        if (openIndex !== -1 && panelRefs.current[openIndex]) {
            const curEl = panelRefs.current[openIndex];
            gsap.killTweensOf(curEl);
            gsap.set(curEl, { overflow: 'hidden', pointerEvents: 'auto' });
            const autoHeight = curEl.scrollHeight || 'auto';
            gsap.fromTo(curEl, {
                height: 0,
                opacity: 0
            }, {
                height: autoHeight,
                opacity: 1,
                duration: 0.4,
                ease: 'power2.out',
                onComplete: () => gsap.set(curEl, { height: 'auto' })
            });
        }

        prevOpenRef.current = openIndex;
    }, [openIndex]);

    const toggleFeature = (index) => {
        setOpenIndex(openIndex === index ? -1 : index);
    };

    return (
        <section className="section services-how-we-work bg-white">
            <Container>
                <div className="how-we-work-grid">
                    {/* Left Side - How We Work */}
                    <div className="how-we-work-left flex flex-col justify-center">
                        <div className="container section-badge flex items-center gap-3 text-[16px] md:text-[20px] !px-1">
                            <div className="badge-dot"></div>
                            How We Work
                        </div>
                        <h2 className="text-2xl md:text-3xl lg:text-4xl">Helping You<br />Navigate</h2>
                        <div className="how-we-work-visual">
                            <img src="/images/contact_us_container.webp" alt="Timeline Express Container" loading="lazy" decoding="async" />
                        </div>
                    </div>

                    {/* Right Side - Features Accordion */}
                    <div className="how-we-work-right">
                        <div className="features-accordion">
                            {features.map((feature, index) => (
                                <div key={index} className={`feature-item ${openIndex === index ? 'open' : ''}`}>
                                    <button
                                        className="feature-toggle !p-2"
                                        aria-expanded={openIndex === index ? 'true' : 'false'}
                                        onClick={() => toggleFeature(index)}
                                    >
                                        <span className="feature-title text-sm md:text-base lg:text-lg">{feature.title}</span>
                                        <span className="feature-icon">{openIndex === index ? '–' : '+'}</span>
                                    </button>
                                    <div
                                        className="feature-panel"
                                        ref={(el) => { panelRefs.current[index] = el; }}
                                    >
                                        <p className="text-sm md:text-base">{feature.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </Container>
        </section>
    );
};

export default ServicesHowWeWork;
